import Survicate from '@survicate/survicate-web-package/survicate_widget';
import { AfterViewInit, Component, ElementRef, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { filter, map, mergeMap, switchMap, take } from 'rxjs/operators';

import { $, cloneDeep, WindowRef } from '@saliente/library';

import { ToastrService } from 'ngx-toastr';
import { ClientFinancialReportsService } from 'src/app/clients/reports/financial/financial-reports.service';
import { GetTakeoverBadgeStatusQuery } from 'src/app/takeover/adapters/takeover.generated';
import { TakeoverService } from 'src/app/takeover/takeover.service';
import { AppMenuModel } from '../appmenu/appmenu.models';
import { AppMenuService } from '../appmenu/appmenu.service';
import { PredefinedUserZone } from '../auth/auth.models';
import { AuthService } from '../auth/auth.service';
import { COMMON_ROUTES } from '../common.routes.constants';
import { ClientContractCommonService } from '../general/clientContractCommon.service';
import { ClientFormsCommonService } from '../general/clientFormsCommon.service';
import { CommonService } from '../general/common.service';
import { ConfigService } from '../general/config.service';
import { DocumentStatisticsCommon } from '../general/documentStatistics.service';
import { EFacturaServiceCommon } from '../general/e-factura.service';
import { EshopOrdersServiceCommon } from '../general/eshop-orders-common.service';
import { ImportServiceCommon } from '../general/import.service';
import { PayrollServiceCommon } from '../general/payroll.service';
import { UploadService } from '../general/upload.service';
import { ChatService } from '../notifications/chat.service';
import {
	AnafMessagesService,
	Subs_GetAnafMessageNotFinalizedCountUsingGetQuery,
	Subs_GetAnafMessageNotFinalizedCountUsingGetQueryVariables,
} from '../services/anaf-messages.service';
import {
	BalanceSheetService,
	Subs_GetBalanceSheetClientWorkInProgressCountForClientUsingGetQuery,
	Subs_GetBalanceSheetClientWorkInProgressCountForClientUsingGetQueryVariables,
	Subs_GetBalanceSheetExpertWorkInProgressCountForClientUsingGetQuery,
	Subs_GetBalanceSheetExpertWorkInProgressCountForClientUsingGetQueryVariables,
} from '../services/balance-sheet.service';
import { HeaderHelpMenuEditorComponent } from './headers/header-help-menu-editor.component';
import { HeaderHelpVideoEditorComponent } from './headers/header-help-video-editor.component';

function coerceBooleanProperty(value: any): boolean {
	return value != null && `${value}` !== 'false';
}

@Component({
    selector: 'masterview',
    templateUrl: 'masterview.component.html',
    styleUrls: ['masterview.component.scss', 'masterview.sidebar.component.scss'],
    standalone: false
})
export class MasterviewComponent implements OnInit, OnDestroy, AfterViewInit {
	public sideBarVisible = false;
	public notifications: any[] = [];

	public PredefinedUserZone = PredefinedUserZone;

	public helpConfig: any = {};
	public helpId: string;
	public helpVideos: any[] = [];
	public helpMenuItems: any[] = [];
	public helpMenuItemsForEdit: any[] = [];
	public helpUserGuides: any[] = [];
	public showHelpMenuEditor = false;
	public showHelpVideoEditor = false;
	public helpVideosForEdit: any[] = [];

	private helpSubscription1: Subscription;
	private helpSubscription2: Subscription;

	private survicateInitPromise: Promise<void> | null = null;


	@ViewChildren('parentMenu') parentMenu: QueryList<ElementRef>;
	@ViewChild(HeaderHelpVideoEditorComponent) videoEditor: HeaderHelpVideoEditorComponent;
	@ViewChild(HeaderHelpMenuEditorComponent) menuEditor: HeaderHelpMenuEditorComponent;

	@Input() public logoSrc: string;
	@Input() public logoAlt: string;

	private _showLanguages = false;
	@Input()
	get showLanguages(): boolean {
		return this._showLanguages;
	}

	set showLanguages(value) {
		this._showLanguages = coerceBooleanProperty(value);
	}

	private __showSideBar__ = true;
	get showSideBar(): boolean {
		return this.__showSideBar__ && this.authService.isEnrolled;
	}
	set showSideBar(value: boolean) {
		this.__showSideBar__ = value;
	}

	private __showHeaderBar__ = true;
	get showHeaderBar(): boolean {
		return this.__showHeaderBar__ && this.authService.isEnrolled;
	}
	set showHeaderBar(value: boolean) {
		this.__showHeaderBar__ = value;
	}

	get unseenMessages(): number {
		if (this.authService.selectedZoneCode === PredefinedUserZone.Administrator) {
			return this.chatService.unseenMessages;
		} else {
			return this.chatService.unansweredMessages;
		}
	}

	get waitingImportList(): number {
		return this.importServiceCommon.waitingImportsCount;
	}

	get missingDocumentsCount(): number {
		return this.financialReports.missingDocumentsCount;
	}

	get clarificationCount(): number {
		return this.financialReports.clarificationCount;
	}

	get cardClarificationCount(): number {
		return this.financialReports.cardClarificationCount;
	}

	get reconciliationCount(): number {
		return this.financialReports.reconciliationCount;
	}

	get documentsCount(): number {
		if (this.isClient) {
			return this.documentStatisticsCommon.notUsableDocumentsCount;
		} else if (this.isAccExpert) {
			return this.documentStatisticsCommon.sentToExpertDocumentsCount;
		}
	}

	get accountingTotalCount(): number {
		let totalCount = 0;
		this.appMenuService.getMenus().forEach((menu) => {
			if (menu.text === 'Contabilitate') {
				menu.childrenMenu.forEach((childMenu: any) => {
					totalCount =
						totalCount +
						(childMenu.isFinancialDocuments ? this.documentsCount : 0) +
						(childMenu.isMissingDocsMenu ? this.missingDocumentsCount : 0) +
						(childMenu.isClarificationMenu ? this.clarificationCount : 0) +
						(childMenu.isReconciliationMenu ? this.reconciliationCount : 0) +
						(childMenu.isBalanceSheet ? (this.isAccExpert ? this.balanceSheetExpertWorkInProgressCount : this.balanceSheetClientWorkInProgressCount) : 0) +
						(childMenu.isAnafComm ? this.anafMessageNotFinalizedCount : 0) +
						(childMenu.isForms ? this.allActiveFormCount : 0) +
						(childMenu.isDUForms ? this.duFormCount : 0);
				});
			}
		});
		return totalCount;
	}
	get portofolioTotalCount(): number {
		let totalCount = 0;
		this.appMenuService.getMenus().forEach((menu) => {
			if (menu.text === 'Portofoliu') {
				menu.childrenMenu.forEach((childMenu: any) => {
					totalCount = totalCount + (childMenu.isTakeoverDashboard ? this.takeoverDashboardBadgeCount : 0);
				});
			}
		});
		return totalCount;
	}

	get balanceSheetWorkInProgressCount(): number {
		if (this.isClient) {
			return this.balanceSheetClientWorkInProgressCount;
		} else if (this.isAccExpert) {
			return this.balanceSheetExpertWorkInProgressCount;
		}

		return 0;
	}

	get eFacturaCount(): number {
		return this.eFacturaServiceCommon.inboxNotVerifiedCount + this.eFacturaServiceCommon.outboxWithErrorCount + this.eFacturaServiceCommon.outboxNotAnalyzedCount;
	}

	get minimumWageEmployeesCount(): number {
		return this.payrollServiceCommon.waitingMinimumWageEmployeeCount;
	}

	get payrollTotalCount(): number {
		var totalCount = 0;
		this.appMenuService.getMenus().forEach((menu) => {
			if (menu.text == 'Salarizare') {
				menu.childrenMenu.forEach((childMenu: any) => {
					totalCount = totalCount + (childMenu.isSalariatiMenu ? this.minimumWageEmployeesCount : 0);
				});
			}
		});
		return totalCount;
	}

	get invoiceTotalCount(): number {
		return this.eFacturaCount;
	}

	get eshopCreatedOrdersCount(): number {
		return this.eshopOrdersServiceCommon.createdOrders;
	}

	get eshopTotalCount(): number {
		return this.eshopCreatedOrdersCount;
	}

	get takeoverCount(): number {
		return this.takeoverService.takeoverBadgeForClientCount;
	}

	get adminMenuTotalCount(): number {
		let totalCount = 0;
		this.appMenuService.getMenus().forEach((menu) => {
			if (menu.text === 'Administrare') {
				menu.childrenMenu.forEach((childMenu: any) => {
					totalCount = totalCount + (childMenu.isTakeover ? this.takeoverCount : 0);
				});
			}
		});
		return totalCount;
	}

	get allActiveFormCount(): number {
		return this.clientFormsCommonService.allActiveFormCount;
	}

	get duFormCount(): number {
		return this.clientFormsCommonService.duActiveFormCount;
	}

	get contractCount(): number {
		return this.clientContractCommonService.contractNotExistsCount + this.clientContractCommonService.extraCostCount + this.clientContractCommonService.unpaidInvoiceCount;
	}

	public currentHelpVideo: any;
	public showModalBox = false;
	public showHelpConfirmationDialog = false;
	currentCompanyEid = '';
	private companyChangeSubscription: Subscription;
	private anafCommSomethingChanged: Subscription;
	anafMessageNotFinalizedCount = 0;
	private balanceSheetSomethingChanged: Subscription;
	balanceSheetExpertWorkInProgressCount = 0;

	private balanceSheetClientSomethingChanged: Subscription;
	balanceSheetClientWorkInProgressCount = 0;

	private takeoverDashboardBadgeCountSomethingChanged: Subscription;
	public takeoverDashboardBadgeCount = 0;

	public isClient: boolean;
	public isAccExpert: boolean;
	public isHRExpert: boolean;
	public isSubcontractor: boolean;
	public hasPayrollAccess: boolean;


	constructor(
		protected winRef: WindowRef,
		public authService: AuthService,
		private configService: ConfigService,
		public commonService: CommonService,
		public appMenuService: AppMenuService,
		public router: Router,
		private chatService: ChatService,
		private route: ActivatedRoute,
		private sanitizer: DomSanitizer,
		private importServiceCommon: ImportServiceCommon,
private toastr: ToastrService,
		private balanceSheetService: BalanceSheetService,
		private anafMessagesService: AnafMessagesService,
		private financialReports: ClientFinancialReportsService,
		private documentStatisticsCommon: DocumentStatisticsCommon,
		private elementRef: ElementRef,
		private payrollServiceCommon: PayrollServiceCommon,
		private eFacturaServiceCommon: EFacturaServiceCommon,
		private eshopOrdersServiceCommon: EshopOrdersServiceCommon,
		private takeoverService: TakeoverService,
		@Inject(PLATFORM_ID) private platformId: Object,
		private clientFormsCommonService: ClientFormsCommonService,
		private uploadService: UploadService,
		private clientContractCommonService: ClientContractCommonService
	) {
		/*this.notificationsService.unread().subscribe((notifications: any) => {
			this.notifications = notifications;
		});*/
	}

	public ngOnInit(): void {
		this.companyChangeSubscription = this.authService.getSelectedCompanyId().subscribe((companyEid) => {
			this.initializeZoneBooleans();

			if (companyEid !== this.currentCompanyEid) {
				this.currentCompanyEid = companyEid;
			}

			this.financialReports.computeDocsCount();
			this.documentStatisticsCommon.computeDocsStatisticsCount();
			this.eFacturaServiceCommon.getEFacturaStatistics();
			if (this.authService.isInRole('ESHOP_ORDER_VIEW')) {
				this.eshopOrdersServiceCommon.getCreatedOrders();
			}
			this.takeoverService.getOpenTakeoverBadgeCount();
			this.clientFormsCommonService.getAllActiveFormCountSubscription();
			this.clientContractCommonService.getSubscriptionBadges();
			this.clientContractCommonService.getClientDocumentsTotalStatisticsForContract();

			if (this.hasPayrollAccess) {
				this.payrollServiceCommon.computeMinimumWageEmployeeCount();
			}

			if (companyEid != null) {
				this.commonService
					.getInventoryEnabled()
					.pipe(take(1))
					.subscribe((isInventoryEnabled) => {
						if (this.authService.isInRole('INVENTORY_EVENT_VIEW')) {
							this.appMenuService.setMenuAndChildrenDisabled('Stocuri', isInventoryEnabled);
						}
					});

				this.commonService
					.getIncorporationType()
					.pipe(take(1))
					.subscribe((code) => {
						if (code == 'PFA' || code == 'PFI' || code == 'IF' || code == 'II') {
							this.appMenuService.setMenuVisibility('Declarații unice', true);
							this.appMenuService.setMenuVisibility('Bilanțuri', false);

							this.clientFormsCommonService.getDuFormCount(companyEid);
						} else {
							this.appMenuService.setMenuVisibility('Declarații unice', false);
							this.appMenuService.setMenuVisibility('Bilanțuri', true);
						}
					});

				this.configService.accountingPartner = ['keez.le','keez.dm', 'keez.st'].includes(this.authService.user.selectedCompany.accountingPartnerCode)
					? null
					: this.authService.user.selectedCompany.accountingPartnerCode;
				// some menus should be not be visible to accounting partners other than Keez
				this.setMenuVisibilityForAccountingPartner();

				this.commonService
					.getIsClientInCancellationOrDissolutionProcess()
					.pipe(take(1))
					.subscribe((isCancellationOrDissolution) => {
						if (!isCancellationOrDissolution) {
							this.openSurvicate();
						}
					});

				this.startImportCount();
			}

			if (this.isClient && this.isAccountSuspended()) {
				this.goToContractPage();
			}

			/*- not all users have BALANCE_SHEET_EDIT roles, especially during the period the Client is suspended*/
			if (this.isClient && this.authService.isInRole('BALANCE_SHEET_EDIT')) {
				const vbc: Subs_GetBalanceSheetClientWorkInProgressCountForClientUsingGetQueryVariables = {
					clientEid: companyEid,
				};
				const bc = this.balanceSheetService.getBalanceSheetClientWorkInProgressCountForClient(vbc);

				bc.subscribe((rez) => {
					this.balanceSheetClientWorkInProgressCount = rez.subs.getBalanceSheetClientWorkInProgressCountForClientUsingGET.count;
				});
			}

			if (!this.authService.isInRole('EXPERT')) {
				return;
			}

			const vb: Subs_GetBalanceSheetExpertWorkInProgressCountForClientUsingGetQueryVariables = {
				clientEid: companyEid,
			};
			const b = this.balanceSheetService.getBalanceSheetExpertWorkInProgressCountForClient(vb);

			const va: Subs_GetAnafMessageNotFinalizedCountUsingGetQueryVariables = {
				clientEid: companyEid,
			};
			const a = this.anafMessagesService.getAnafMessageNotFinalizedCount(va);

			const t = this.takeoverService.getTakeoverBadgeStatusObservable();

			forkJoin({ b, a, t }).subscribe((rez) => {
				this.balanceSheetExpertWorkInProgressCount = rez.b.subs.getBalanceSheetExpertWorkInProgressCountForClientUsingGET.count;
				this.anafMessageNotFinalizedCount = rez.a.subs.getAnafMessageNotFinalizedCountUsingGET.count;
				this.takeoverDashboardBadgeCount = rez.t.accounts.getTakeoverBadgeStatusUsingGET.count;
			});
		});

		this.anafCommSomethingChanged = this.anafMessagesService.somethingChanged
			.pipe(
				filter((rez) => this.authService.isInRole('EXPERT')),
				switchMap((rez): Observable<Subs_GetAnafMessageNotFinalizedCountUsingGetQuery> => {
					if (rez === true) {
						const va: Subs_GetAnafMessageNotFinalizedCountUsingGetQueryVariables = {
							clientEid: this.currentCompanyEid,
						};
						return this.anafMessagesService.getAnafMessageNotFinalizedCount(va);
					} else {
						return of(null);
					}
				}),
				map((rez) => {
					if (rez != null) {
						this.anafMessageNotFinalizedCount = rez.subs.getAnafMessageNotFinalizedCountUsingGET.count;
					}
				})
			)
			.subscribe();

		this.balanceSheetSomethingChanged = this.balanceSheetService.somethingChanged
			.pipe(
				switchMap((rez): Observable<Subs_GetBalanceSheetExpertWorkInProgressCountForClientUsingGetQuery> => {
					if (rez === true) {
						const va: Subs_GetBalanceSheetExpertWorkInProgressCountForClientUsingGetQueryVariables = {
							clientEid: this.currentCompanyEid,
						};
						return this.balanceSheetService.getBalanceSheetExpertWorkInProgressCountForClient(va);
					} else {
						return of(null);
					}
				}),
				map((rez) => {
					if (rez != null) {
						this.balanceSheetExpertWorkInProgressCount = rez.subs.getBalanceSheetExpertWorkInProgressCountForClientUsingGET.count;
					}
				})
			)
			.subscribe();

		this.balanceSheetClientSomethingChanged = this.balanceSheetService.somethingChangedClient
			.pipe(
				switchMap((rez): Observable<Subs_GetBalanceSheetClientWorkInProgressCountForClientUsingGetQuery> => {
					if (rez === true) {
						const va: Subs_GetBalanceSheetClientWorkInProgressCountForClientUsingGetQueryVariables = {
							clientEid: this.currentCompanyEid,
						};
						return this.balanceSheetService.getBalanceSheetClientWorkInProgressCountForClient(va);
					} else {
						return of(null);
					}
				}),
				map((rez) => {
					if (rez != null) {
						this.balanceSheetClientWorkInProgressCount = rez.subs.getBalanceSheetClientWorkInProgressCountForClientUsingGET.count;
					}
				})
			)
			.subscribe();

		this.takeoverDashboardBadgeCountSomethingChanged = this.takeoverService.somethingChanged
			.pipe(
				switchMap((rez): Observable<GetTakeoverBadgeStatusQuery> => {
					if (rez === true) {
						return this.takeoverService.getTakeoverBadgeStatusObservable();
					} else {
						return of(null);
					}
				}),
				map((rez) => {
					if (rez != null) {
						this.takeoverDashboardBadgeCount = rez.accounts.getTakeoverBadgeStatusUsingGET.count;
					}
				})
			)
			.subscribe();

		const subscribe = (obs: Observable<any>): Subscription => {
			return obs
				.pipe(
					map(() => this.route),
					map((route: ActivatedRoute) => {
						while (route.firstChild) {
							route = route.firstChild;
						}
						return route;
					}),
					filter((route: ActivatedRoute) => route.outlet === 'primary'),
					mergeMap((route: ActivatedRoute) => route.data.pipe(map(data => ({ data, route }))))
				)
				.subscribe(({ data, route }: { data: any, route: ActivatedRoute }) => {
					if (data?.helpMenu) {
						this.helpId = data.helpMenu;
					} else {
						const segments = route.snapshot.pathFromRoot
							.reduce((acc: string[], r) => acc.concat(r.routeConfig?.path?.split("/") ?? []), [])
							.filter(seg => seg && !seg.startsWith(":"));
						this.helpId = segments.join("_") || undefined;
					}
					this.processHelpMenu(true);
				});
		};

		this.helpSubscription1 = subscribe(this.route.data);
		this.helpSubscription2 = subscribe(this.router.events.pipe(filter((event) => event instanceof NavigationEnd)));

		this.commonService
			.dictionaryStream('HelpSuggestions')
			.pipe(take(1))
			.subscribe((data) => {
				this.helpConfig = data;
				this.processHelpMenu(true);
			});
	}

	public ngAfterViewInit() {
		this.parentMenu.changes.subscribe(() => {
			this.initLazy();
		});

	}

	public initLazy(): void {
		//console.log("initLazy");
		setTimeout(() => {
			$('.collapsible').collapsible();
		}, 1000);
	}

	public ngOnDestroy(): void {
		if (this.balanceSheetSomethingChanged != null) {
			this.balanceSheetSomethingChanged.unsubscribe();
			this.balanceSheetSomethingChanged = null;
		}
		if (this.anafCommSomethingChanged != null) {
			this.anafCommSomethingChanged.unsubscribe();
			this.anafCommSomethingChanged = null;
		}
		if (this.balanceSheetClientSomethingChanged != null) {
			this.balanceSheetClientSomethingChanged.unsubscribe();
			this.balanceSheetClientSomethingChanged = null;
		}
		if (this.companyChangeSubscription != null) {
			this.companyChangeSubscription.unsubscribe();
			this.companyChangeSubscription = null;
		}
		if (this.helpSubscription1) {
			this.helpSubscription1.unsubscribe();
			this.helpSubscription1 = null;
		}
		if (this.helpSubscription2) {
			this.helpSubscription2.unsubscribe();
			this.helpSubscription2 = null;
		}
		if (this.takeoverDashboardBadgeCountSomethingChanged != null) {
			this.takeoverDashboardBadgeCountSomethingChanged.unsubscribe();
			this.takeoverDashboardBadgeCountSomethingChanged = null;
		}
	}

	//#region help menu
	processHelpMenu(autoplayVideo: boolean = false): void {
		// process help videos
		const autoplayVideoString = autoplayVideo ? '?autoplay=1' : '';
		this.helpVideos = [];

		if (this.helpConfig && this.helpId && this.helpConfig.videos && this.helpConfig.videos[this.helpId]) {
			const videos = this.helpConfig.videos[this.helpId];
			if (videos && videos.length) {
				this.helpVideos = videos.map((v: any) => {
					if (this.isContentTypeVideo(v)) {
						return v;
					}
					else {
						const { url, ...props } = v;
						return {
							url: url,
							...props,
						};
					}
				});
			}
		}

		// process help menu items
		this.helpMenuItems = [];
		if (!this.isSubcontractor && this.helpConfig?.menus && this.helpId) {
			this.helpMenuItems = (this.helpConfig.menus[this.helpId] || []).filter((m: any) => m.client !== false);
		}

		// process user guides based on role
		this.helpUserGuides = [];
		if (!this.isSubcontractor) {
			this.helpUserGuides.push({ title: 'Ghidul de utilizare', description: 'Poți consulta ghidul de utilizare', link: 'https://app.keez.ro/help/client/index.html' });
		}
		if (this.isAccExpert) {
			this.helpUserGuides.push({ title: 'Manualul expertului Keez', description: 'Instrucțiuni detaliate pentru Experti', link: 'https://app.keez.ro/help/expert/index.html' });
		}
		if (this.isHRExpert) {
			this.helpUserGuides.push({ title: 'Manualul expertului Keez', description: 'Instrucțiuni detaliate pentru Experti', link: 'https://app.keez.ro/help/expert-payroll/' });
		}
		if (this.isSubcontractor) {
			this.helpUserGuides.push({ title: 'Manualul operatorului Keez', description: 'Instrucțiuni detaliate pentru Operatori', link: 'https://app.keez.ro/help/operator/index.html' });
		}
	}

	public isContentTypeVideo(helpVideo: any): boolean {
		return helpVideo.type === "content";
	}

	public openHelpMenuEditor() {
		this.showHelpMenuEditor = true;
		this.helpMenuItemsForEdit = cloneDeep(this.helpMenuItems);
	}

	public closeHelpMenuEditor() {
		if (this.menuEditor?.inEdit) {
			this.menuEditor.cancelItemEdit();
		} else {
			this.showHelpMenuEditor = false;
		}
	}

	public onHelpMenuSave(menuItems: any[]): void {
		this.helpMenuItems = menuItems;
		this.closeHelpMenuEditor();
		this.saveHelpData('menu');
	}

	public openHelpVideoEditor() {
		this.showHelpVideoEditor = true;
		this.helpVideosForEdit = cloneDeep(this.helpVideos);
	}

	public closeHelpVideoEditor() {
		if (this.videoEditor?.inEdit) {
			this.videoEditor.cancelItemEdit();
		} else {
			this.showHelpVideoEditor = false;
		}
	}

	public onHelpVideoSave(videos: any[]): void {
		this.helpVideos = videos;
		this.closeHelpVideoEditor();
		this.saveHelpData('video');
	}

	public saveHelpData(type: 'menu' | 'video'): void {
		const updatedConfig = { ...this.helpConfig };
		if (type === 'menu') {
			updatedConfig.menus = { ...this.helpConfig.menus, [this.helpId]: this.helpMenuItems };
		} else {
			updatedConfig.videos = { ...this.helpConfig.videos, [this.helpId]: this.helpVideos };
		}
		const blob = new Blob([JSON.stringify(updatedConfig)], { type: 'application/json' });
		const meta = {
			fileName: 'HelpSuggestions.json',
			documentType: 'JSON',
			documentEid: 'HelpSuggestions',
			category: 'Public',
			fileType: 'application/json'
		};
		this.configService.getDocumentsUploadUrl().pipe(
			take(1),
			switchMap(baseUrl =>
				this.uploadService.upload(baseUrl + '/files', blob as any, meta).pipe(
					filter(data => !!data.done),
					take(1)
				)
			)
		).subscribe(() => {
			this.helpConfig = updatedConfig;
			this.processHelpMenu();
		});
	}
	//#endregion
	
	public closeHideHelpConfirmationDialog(): void {
		this.showHelpConfirmationDialog = false;
	}

	public hideHelpVideos(): void {
		this.showHelpConfirmationDialog = true;
		this.authService.user.showHelp = false;
		this.authService.updateMyData().pipe(take(1)).subscribe();
	}

	public toggleFullScreen(): void {
		this.winRef.toggleFullScreen();
	}

	public toggleSidebar(): void {
		if (this.sideBarVisible) {
			this.winRef.nativeWindow.document.body.overflow = 'hidden';
			this.sideBarVisible = false;
		} else {
			delete this.winRef.nativeWindow.document.body.overflow;
			this.sideBarVisible = true;
		}
	}

	public toggleMenu(menu: AppMenuModel): void {
		if (menu.childrenMenu && menu.childrenMenu.length) {
			menu.canExpand = !menu.canExpand;
		}
	}

	public async selectCompanyId(companyId: string): Promise<void> {
		await this.authService.user.setSelectedCompanyId(companyId).toPromise();
		const company = this.authService.user.selectedCompany;
		if (company) {
			//this.configService.accountingPartner = company.accountingPartnerCode !== 'keez' ? company.accountingPartnerCode : null;
			this.configService.accountingPartner = ['keez.le','keez.dm', 'keez.st'].includes(company.accountingPartnerCode) ? null : company.accountingPartnerCode;
		}

		if (this.authService.selectedZoneCode === PredefinedUserZone.Administrator) {
			this.chatService.clearChats();
		}
		return of<void>().toPromise();
	}

	// some menus should be not be visible to accounting partners other than Keez
	public setMenuVisibilityForAccountingPartner() {
		if (this.configService.accountingPartner == null) {
			this.appMenuService.setMenuVisibility('Contract Keez', true);
		} else {
			this.appMenuService.setMenuVisibility('Contract Keez', false);
		}

		if (
			this.configService.accountingPartner == null ||
			this.authService.user.selectedCompany.fiscalNumber == '26301716' ||
			this.authService.user.selectedCompany.fiscalNumber == '12345678'
		) {
			this.appMenuService.setMenuAndChildrenVisibility('Magazin online', true);
		} else {
			this.appMenuService.setMenuAndChildrenVisibility('Magazin online', false);
		}
	}

	public switchZone(zoneCode: PredefinedUserZone): void {
		this.authService.selectedZoneCode = zoneCode;
		this.initializeZoneBooleans();

		const homePage = this.appMenuService.getHomePage();
		if (homePage) {
			this.router.navigate(homePage);
			this.chatService.clearChats();
		}
	}

	public initializeZoneBooleans() {
		this.isClient = this.authService.selectedZoneCode === this.PredefinedUserZone.Administrator;
		this.isAccExpert = this.authService.selectedZoneCode === this.PredefinedUserZone.Expert;
		this.isHRExpert = this.authService.selectedZoneCode === this.PredefinedUserZone.HrExpert;
		this.isSubcontractor = this.authService.selectedZoneCode === this.PredefinedUserZone.Subcontractor;
		this.hasPayrollAccess = this.authService.isInRole('EMPLOYEE_VIEW');
	}

	public gotoNotification(notificationId?: string): void {
		const commands = ['/clients', 'notifications'];
		if (notificationId) {
			commands.push(notificationId);
		}
		this.router.navigate(commands);
	}

	public gotoProfile(): void {
		this.router.navigate(['/profile']);
	}

	public gotoContact(): void {
		this.router.navigate([COMMON_ROUTES.CONTACT_PAGE]);
	}

	public makeSpecificMenuActive() {
		this.appMenuService.getMenus().forEach((menu) => {
			let menuName = menu.text.toString();
			let menuElement = document.getElementById(menuName);
			if (menuElement != undefined) {
				let firstChild = menuElement.querySelector('a');

				//remove 'active' from all menu
				if (menuElement.classList.contains('active')) {
					menuElement.classList.remove('active');
				}

				if (firstChild != null && firstChild.classList.contains('active')) {
					firstChild.classList.remove('active');
				}

				//add 'active' to menu.isAtFirstExpanded
				if (menu.isAtFirstExpanded) {
					menuElement.classList.add('active');
					if (firstChild != null) firstChild.classList.add('active');
				}
			}
		});
	}

	public makeAlwaysExpandedElementsActive(target: any) {
		this.appMenuService.getMenus().forEach((menu) => {
			if (menu.alwaysExpanded) {
				let menuName = menu.text.toString();
				var parentElement = document.getElementById(menuName);

				if (target) {
					// traverses the Element and its parents (heading toward the document root) until it finds a node that matches the provided selector string
					var parentTarget = target.closest(`#${menuName}`) as Element;
					// parentTarget found
					if (parentTarget != null) {
						// get all active elements and remove active class
						var activeElements = document.getElementsByClassName('active');
						if (activeElements != null && activeElements.length > 0) {
							activeElements[0].classList.remove('active');
						}

						// add active class to parent element
						parentTarget.classList.add('active');
					} else {
						parentElement.classList.remove('active');
					}
				}
			}
		});
	}

	public async openSurvicate(): Promise<void> {
		if (this.configService.environment != 'prod') {
			return;
		}

		if (
			this.authService.isInRole('EXPERT_MARKER') ||
			this.authService.isInRole('HR_EXPERT_MARKER') ||
			this.authService.isInRole('SUB_DIGITIZER_AGENT') ||
			this.authService.isInRole('SUB_VALIDATOR_AGENT')
		) {
			return;
		}

		if (!this.survicateInitPromise) {
			this.survicateInitPromise = Survicate.init({
				workspaceKey: '5eb369b727c1efd31f5c4989228a2149',
				forcedLanguage: 'ro',
				disableSensitiveDataPersistence: true
			});
		}

		await this.survicateInitPromise;

		this.authService.getUser().subscribe((user) => {
			if (user) {
				Survicate.setVisitorTraits({
					user_id: user.externalId,
					email: user.email,
					created_at: Math.floor(new Date(user.created).getTime() / 1000),
				});
				Survicate.retarget();
			}
		});
	}



	public resetBadges() {}

	public hasGlobalMessage() {
		return this.hasGlobalWarningMessage || this.hasGlobalImportantMessage();
	}

	// Modificari fiscale din 01.01.2023
	public hasGlobalImportantMessage() {
		return false;
	}

	public hasGlobalWarningMessage() {
		return this.authService.globalWarningMessage;
	}

	public hasOnrcRecordStatusMessage() {
		return this.authService.onrcRecordStatusMessage;
	}

	public openLink() {
		window.open('https://bit.ly/alerta_modificari_fiscale', '_blank', 'noopener noreferrer');
	}

	public isAccountSuspended() {
		return this.authService.user && this.authService.user.selectedCompany.suspended;
	}

	public goToContractPage() {
		const commands = ['/clients', 'clientcontract', 'contract'];
		this.router.navigate(commands);
	}

	public startImportCount() {
		const menus = this.appMenuService.getMenus();
		const parentMenu = menus.find((m: any) => m.text === 'Importuri'); // search in parent menus
		if (parentMenu != undefined) {
			if (!parentMenu.disabled) {
				this.importServiceCommon.startImportCount();
			}
		} else {
			// search in children menus
			for (let parentMenu of menus) {
				if (parentMenu.childrenMenu != undefined) {
					const childMenu = parentMenu.childrenMenu.find((m: any) => m.text === 'Importuri');
					if (childMenu != null && !childMenu.disabled) {
						this.importServiceCommon.startImportCount();
						break;
					}
				}
			}
		}
	}
}
